
import React, { useState } from "react";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { UserWithProfile } from "@/models/user";

interface DeleteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserWithProfile | null;
  onUserDeleted: () => void;
}

const DeleteUserDialog: React.FC<DeleteUserDialogProps> = ({
  open,
  onOpenChange,
  user,
  onUserDeleted
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!user) {
      toast.error("No se ha seleccionado ningún usuario para eliminar");
      return;
    }
    
    setIsDeleting(true);
    console.log("Iniciando proceso de eliminación para el usuario:", user.id);
    
    try {
      // Eliminar el perfil del usuario de la tabla profiles
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', user.id);
        
      if (profileError) {
        console.error("Error al eliminar perfil:", profileError);
        throw new Error(`Error al eliminar usuario: ${profileError.message}`);
      }
      
      console.log("Usuario eliminado exitosamente del sistema");
      toast.success(`Usuario ${user.profile?.full_name || user.email} eliminado correctamente`);
      
      // Cerrar el diálogo primero
      onOpenChange(false);
      
      // Luego actualizar la lista
      setTimeout(() => {
        onUserDeleted();
      }, 100);
      
    } catch (error) {
      console.error("Error en el proceso de eliminación:", error);
      const errorMessage = error instanceof Error ? error.message : "Error desconocido";
      toast.error(`Error al eliminar usuario: ${errorMessage}`);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
          <AlertDialogDescription>
            Esta acción eliminará permanentemente el usuario {user?.profile?.full_name || user?.email} y no se puede deshacer.
            {user?.profile?.role === 'admin' && (
              <span className="block mt-2 text-red-600 font-medium">
                ⚠️ Advertencia: Estás eliminando un usuario administrador.
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={confirmDelete}
            disabled={isDeleting}
            className="bg-red-500 hover:bg-red-600 flex items-center gap-2"
          >
            {isDeleting && <Loader2 className="h-4 w-4 animate-spin" />}
            {isDeleting ? "Eliminando..." : "Eliminar"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteUserDialog;
